'use client'

import { useState, useContext, useEffect, useRef } from 'react'
import { ApiContext } from '../../hooks/api'
import Button from '../Button'
import ContextMenu from '../ContextMenu'
import TextField from '../Form/TextField'
import { useForm } from '../../hooks'
import { doc, updateDoc } from 'firebase/firestore'
import Modal from '../Modal'
import MFAEnable from '../MFAEnable'
import { db, storage } from '../../firebase'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { v4 as uuidv4 } from 'uuid'

const MyAccount = () => {
  const { user } = useContext(ApiContext)
  const [isEditing, setIsEditing] = useState(false)
  const [showMfaModal, setShowMfaModal] = useState(false)
  const [showActionDropdown, setShowActionDropdown] = useState(false)
  const [mfaStatus, setMfaStatus] = useState(user?.isTwoFactorEnabled || false)
  const [profileUrl, setProfileImage] = useState(
    user?.profileUrl ||
      'https://www.londondentalsmiles.co.uk/wp-content/uploads/2017/06/person-dummy.jpg'
  )
  const [dutyStatus, setDutyStatus] = useState(user?.dutyStatus || false)
  const [chatNotification, setChatNotification] = useState(
    user?.chatNotification !== false
  )
  const [isUploading, setIsUploading] = useState(false)
  const dropdownRef = useRef(null)

  const [getFieldProps, formData, formError, resetForm] = useForm({
    email: '',
    displayName: '',
    firstName: '',
    lastName: ''
  })

  useEffect(() => {
    resetForm({
      email: user?.email || '',
      displayName: user?.displayName || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    })
    setProfileImage(
      user?.profileUrl ||
        'https://www.londondentalsmiles.co.uk/wp-content/uploads/2017/06/person-dummy.jpg'
    )
    setDutyStatus(user?.dutyStatus || false)
    setChatNotification(user?.chatNotification !== false)
    setMfaStatus(user?.isTwoFactorEnabled || false)
  }, [user, resetForm])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowActionDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const uploadImageToStorage = async (file) => {
    if (!user?.id) return null

    try {
      setIsUploading(true)
      const storageRef = ref(storage, `stage/users/${user.id}`)
      const snapshot = await uploadBytes(storageRef, file)
      const downloadURL = await getDownloadURL(snapshot.ref)
      return downloadURL
    } catch (error) {
      console.error('Error uploading image: ', error)
      return null
    } finally {
      setIsUploading(false)
    }
  }

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]
    if (file && user) {
      // Check if file is an image
      if (!file.type.match('image.*')) {
        alert('Please select an image file')
        return
      }

      // Check file size (e.g., 5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size should be less than 5MB')
        return
      }

      const reader = new FileReader()
      reader.onload = () => setProfileImage(reader.result)
      reader.readAsDataURL(file)

      // Upload to Firebase Storage
      const downloadURL = await uploadImageToStorage(file)
      if (downloadURL) {
        setProfileImage(downloadURL)
      }
    }
  }

  const handleMfaToggle = async (enabled) => {
    if (!isEditing) return
    if (!user?.id) {
      console.error('No user ID available')
      return
    }
    if (!enabled) {
      try {
        const userDocRef = doc(db, 'users', user.id)
        await updateDoc(userDocRef, {
          isTwoFactorEnabled: false,
          twoFactorRecoveryCodes: [],
          secretKey: null
        })
        setMfaStatus(false)
        setShowMfaModal(false)
      } catch (error) {
        console.error('Error disabling MFA: ', error.message, error.code)
      }
    } else {
      setShowMfaModal(true)
    }
  }

  const handleToggleDutyStatus = async () => {
    if (!isEditing) return

    const newStatus = !dutyStatus
    setDutyStatus(newStatus)

    if (user?.id) {
      try {
        await updateDoc(doc(db, 'users', user.id), {
          dutyStatus: newStatus
        })
      } catch (error) {
        console.error('Error updating duty status: ', error)
        setDutyStatus(!newStatus)
      }
    }
  }

  const handleToggleChatNotification = async () => {
    if (!isEditing) return

    const newStatus = !chatNotification
    setChatNotification(newStatus)

    if (user?.id) {
      try {
        await updateDoc(doc(db, 'users', user.id), {
          chatNotification: newStatus
        })
      } catch (error) {
        console.error('Error updating chat notification: ', error)
        setChatNotification(!newStatus)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (isEditing && user?.id) {
      const fullname = `${formData.firstName} ${formData.lastName}`.trim()
      const updatedFields = {
        ...(formData.email !== user.email && { email: formData.email }),
        ...(formData.displayName !== user.displayName && {
          displayName: formData.displayName
        }),
        ...(formData.firstName !== user.firstName && {
          firstName: formData.firstName
        }),
        ...(formData.lastName !== user.lastName && {
          lastName: formData.lastName
        }),
        ...(fullname !== `${user.firstName} ${user.lastName}` && { fullname }),
        ...(profileUrl !== user.profileUrl && { profileUrl }),
        dutyStatus,
        chatNotification
      }

      try {
        if (Object.keys(updatedFields).length > 0) {
          await updateDoc(doc(db, 'users', user.id), updatedFields)
        }
        setIsEditing(false)
        setShowActionDropdown(false)
      } catch (error) {
        console.error('Error updating document: ', error.message, error.code)
      }
    }
  }

  const handleCancel = () => {
    setIsEditing(false)
    setShowActionDropdown(false)
    resetForm({
      email: user?.email || '',
      displayName: user?.personalData?.[0]?.value || '',
      firstName: user?.firstName || '',
      lastName: user?.lastName || ''
    })
    setProfileImage(
      user?.profileUrl ||
        'https://www.londondentalsmiles.co.uk/wp-content/uploads/2017/06/person-dummy.jpg'
    )
    setDutyStatus(user?.dutyStatus || false)
    setChatNotification(user?.chatNotification !== false)
  }

  const handleEditClick = () => {
    setIsEditing(true)
    setShowActionDropdown(false)
  }

  if (!user)
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-lg text-gray-600'>Loading user data...</div>
      </div>
    )

  return (
    <div className='bg-gray-50 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='container mx-auto'>
        {/* Header */}
        <div className='bg-white rounded-xl shadow-sm border border-gray-200 mb-5'>
          <div className='px-6 py-8 sm:px-8'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between'>
              <div className='flex items-center space-x-6'>
                <div className='relative'>
                  <img
                    src={profileUrl || '/placeholder.svg'}
                    alt='Profile'
                    className='!w-20 !h-20 rounded-full object-cover border-3 border-gray-200'
                  />
                  {isEditing && (
                    <label className='absolute -bottom-1 -right-1 bg-blue-600 text-white rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors shadow-lg'>
                      {isUploading ? (
                        <svg
                          className='w-4 h-4 animate-spin'
                          fill='none'
                          viewBox='0 0 24 24'>
                          <circle
                            className='opacity-25'
                            cx='12'
                            cy='12'
                            r='10'
                            stroke='currentColor'
                            strokeWidth='4'></circle>
                          <path
                            className='opacity-75'
                            fill='currentColor'
                            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                        </svg>
                      ) : (
                        <svg
                          className='w-4 h-4'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'>
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth='2'
                            d='M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z'
                          />
                        </svg>
                      )}
                      <input
                        type='file'
                        accept='image/*'
                        className='hidden'
                        onChange={handleImageUpload}
                        disabled={isUploading}
                      />
                    </label>
                  )}
                </div>
                <div>
                  <h1 className='text-2xl font-bold text-gray-900'>
                    {user?.displayName || 'User Name'}
                  </h1>
                  <p className='text-gray-600 mt-1'>{user?.email}</p>
                  <div className='flex items-center mt-2'>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        dutyStatus
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                      {dutyStatus ? 'On Duty' : 'Off Duty'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              <div className='mt-4 sm:mt-0 relative' ref={dropdownRef}>
                {isEditing ? (
                  <div className='flex flex-col sm:flex-row gap-3 sm:justify-end'>
                    <button
                      type='button'
                      onClick={handleCancel}
                      className='w-full sm:w-auto px-6 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-lg font-medium'>
                      Cancel
                    </button>
                    <button
                      type='submit'
                      form='account-form'
                      className='w-full sm:w-auto px-6 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg font-medium'
                      disabled={isUploading}>
                      {isUploading ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowActionDropdown(!showActionDropdown)}
                    className='inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'>
                    Actions
                    <svg
                      className='ml-2 -mr-1 w-4 h-4'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'>
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                        d='M19 9l-7 7-7-7'
                      />
                    </svg>
                  </button>
                )}

                {!isEditing && showActionDropdown && (
                  <div className='absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10'>
                    <div className='py-1'>
                      <button
                        onClick={handleEditClick}
                        className='block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100'>
                        Edit Info
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <form id='account-form' onSubmit={handleSubmit} className='space-y-6'>
          {/* Profile Information */}
          <div className='bg-white rounded-xl shadow-sm border border-gray-200'>
            <div className='p-5 sm:px-8'>
              <h2 className='text-lg font-semibold text-gray-900 mb-3'>
                Profile Information
              </h2>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                <TextField
                  label='Email'
                  name='email'
                  type='email'
                  required
                  disabled={true}
                  autoComplete='off'
                  placeholder='Enter your email'
                  {...getFieldProps('email')}
                  className='input'
                />
                <TextField
                  label='Badge'
                  name='displayName'
                  type='text'
                  required
                  disabled={!isEditing}
                  autoComplete='off'
                  placeholder='Enter your display name'
                  {...getFieldProps('displayName')}
                  className='input'
                />
                <TextField
                  label='First Name'
                  name='firstName'
                  type='text'
                  disabled={!isEditing}
                  autoComplete='off'
                  placeholder='Enter your first name'
                  {...getFieldProps('firstName')}
                  className='input'
                />
                <TextField
                  label='Last Name'
                  name='lastName'
                  type='text'
                  disabled={!isEditing}
                  autoComplete='off'
                  placeholder='Enter your last name'
                  {...getFieldProps('lastName')}
                  className='input'
                />
              </div>
            </div>
          </div>

          {/* Settings */}
          <div className='bg-white rounded-xl shadow-sm border border-gray-200'>
            <div className='p-5 sm:px-8'>
              <h2 className='text-lg font-semibold text-gray-900 mb-3'>
                Settings
              </h2>
              <div className='space-y-6'>
                {/* Two-Factor Authentication */}
                <div className='flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0'>
                  <div className='flex-1'>
                    <h3 className='text-sm font-medium text-gray-900'>
                      Two-Factor Authentication
                    </h3>
                    <p className='text-sm text-gray-500 mt-1'>
                      {mfaStatus
                        ? 'Extra security is enabled for your account'
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                  <button
                    type='button'
                    onClick={() => handleMfaToggle(!mfaStatus)}
                    disabled={!isEditing}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                      mfaStatus
                        ? 'bg-red-100 text-red-700 hover:bg-red-200'
                        : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                    } ${
                      !isEditing
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}>
                    {mfaStatus ? 'Disable' : 'Enable'}
                  </button>
                </div>

                {/* Duty Status */}
                <div className='flex items-center justify-between py-4 border-b border-gray-200 last:border-b-0'>
                  <div className='flex-1'>
                    <h3 className='text-sm font-medium text-gray-900'>
                      Duty Status
                    </h3>
                    <p className='text-sm text-gray-500 mt-1'>
                      {dutyStatus
                        ? 'You are currently available for work'
                        : 'You are currently unavailable'}
                    </p>
                  </div>
                  <button
                    type='button'
                    onClick={handleToggleDutyStatus}
                    disabled={!isEditing}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      dutyStatus ? 'bg-blue-600' : 'bg-gray-200'
                    } ${
                      !isEditing
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        dutyStatus ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Chat Notifications */}
                <div className='flex items-center justify-between py-4'>
                  <div className='flex-1'>
                    <h3 className='text-sm font-medium text-gray-900'>
                      Chat Notifications
                    </h3>
                    <p className='text-sm text-gray-500 mt-1'>
                      {chatNotification
                        ? 'You will receive chat notifications'
                        : 'Chat notifications are disabled'}
                    </p>
                  </div>
                  <button
                    type='button'
                    onClick={handleToggleChatNotification}
                    disabled={!isEditing}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      chatNotification ? 'bg-blue-600' : 'bg-gray-200'
                    } ${
                      !isEditing
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}>
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        chatNotification ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {showMfaModal && (
        <Modal onClose={() => setShowMfaModal(false)}>
          <MFAEnable
            user={user}
            onClose={(enabled) => {
              setMfaStatus(enabled)
              setShowMfaModal(false)
            }}
          />
        </Modal>
      )}
    </div>
  )
}

export default MyAccount
